import React, { useState, useEffect } from "react";
import { RouteComponentProps, useLocation } from "@reach/router";
import "react-spinning-wheel/dist/style.css";
import { BasicLayout } from "layouts";
import { debounce } from 'lodash';
import {
  AdvanceSearch,
  SearchBar,
  CustomerTable,
  Pagination,
} from "components";
import { searchCustomer, filterCustomer } from "api";
import { useCustomerContext } from "contexts/Customer";
import { navigate } from "@reach/router";
import { useAuthContext } from "contexts";
import { useNotifications } from "reapop";
import "./style.scss";

export const DashboardPage: React.FunctionComponent<RouteComponentProps> = (
  props
) => {
  const {
    pageIndex,
    pageCount,
    customers,
    isFiltered,
    searchKey,
    advanceOpen,
    searchFields,
    searchClicked,
    updatePageIndex,
    updatePageCount,
    updateCustomers,
    updateSearchFields,
    updateIsFiltered,
    updateSearchKey,
    updateAdvanceOpen,
    updateSearchClicked,
    clearCustomerData,
  } = useCustomerContext();
  const location = useLocation();
  const { notify } = useNotifications();
  const { isLogined, setCurrentPage, logout } = useAuthContext();
  const [searching, setSearching] = useState<boolean>(false);
  const [pageSize] = useState<number>(10);

  useEffect(() => {
    if (!isLogined) {
      navigate("/");
    } else {
      setCurrentPage(location.pathname);
    }
    return () => {};
  }, [isLogined]);

  const getCustomers = async (searchTerm, type, page) => {
    try {
      setSearching(true);
      let res = await callCustomerApi(searchTerm, type, page);
      // need access token | Invalid token identifier
      if (
        res?.response?.data?.errorCode == 401 ||
        res?.response?.data?.errorCode == "E401_TOKEN_EXPIRED"
      ) {
        navigate("/");
        await logout();
        await clearCustomerData();
        return;
      }
      setSearching(false);
      await updateAdvanceOpen(false);
      let totalPageCount;
      if (res?.data?.count) {
        const totalCount = res.data.count;

        if (totalCount % pageSize !== 0) {
          totalPageCount = (totalCount - (totalCount % pageSize)) / pageSize + 1;
        } else {
          totalPageCount = totalCount / pageSize;
        }

        await updatePageCount(totalPageCount);
      }
      // set customers
      if (res?.data?.result && res?.data?.result.length === 0) {
        await updateCustomers([]);
        await updatePageIndex(0);
        await updatePageCount(0);
        return;
      }
      await updateCustomers(res.data.result);
      await updatePageIndex(page);
    } catch (error: any) {
      setSearching(false);
      console.log(error);
      notify("There's a problem loading customer data.", "error", {
        title: "API Error",
      });
    }
  };

  const callCustomerApi = async (searchTerm, type, page) => {
    // searchCustomer | filterCustomer api
    let res;
    switch (type) {
      case 0: // get customers
        await updateIsFiltered(false);
        res = await searchCustomer(searchTerm, pageSize, page);
        break;
      case 1: // filter customers
        await updateIsFiltered(true);
        if (searchFields.filter(s => s.value === "").length === searchFields.length) {
          res = await searchCustomer("", pageSize, page);
        } else {
          res = await filterCustomer(pageSize, page, searchFields);
        }
        break;
    }

    if (res.status !== 200) {
      notify("There's a problem loading customer data.", "error", {
        title: "API Error",
      });
    }

    return res;
  };

  const loadPage = async (currentPage) => {
    if (!isFiltered) {
      getCustomers(searchKey, 0, currentPage);
    } else {
      getCustomers("", 1, currentPage);
    }
  };

  const loadPageDebounce = debounce(function (currentPage) {
    loadPage(currentPage);
  }, 800);

  const onFilter = async (data) => {
    await updateSearchClicked(true);
    await updateSearchFields(data);
    getCustomers("", 1, 1);
  };

  const onSearchCustomer = async (val) => {
    await updateSearchKey(val);
    getCustomers(val, 0, 1);
  };

  return (
    <BasicLayout isSpinner={searching}>
      <div className={!searching ? "main-container" : "main-container-opacity"}>
        {advanceOpen && (
          <AdvanceSearch
            cancelSearch={async () => {
              await updateAdvanceOpen(false);
            }}
            confirmFilter={onFilter}
          />
        )}
        <SearchBar
          keyword={searchKey}
          onSearch={(val: string) => onSearchCustomer(val)}
          showAdvanceSearch={async () => await updateAdvanceOpen(true)}
        />
        <CustomerTable customers={customers} />
        {customers.length == 0 && (
          <div className="search-help">
            <span>
              {searchClicked
                ? "No Customer Found!"
                : "Please search customers by filter options"}
            </span>
          </div>
        )}
        <Pagination
          pageIndex={pageIndex}
          pageCount={pageCount}
          setCurrentPage={(val) => loadPageDebounce(val)}
        />
      </div>
    </BasicLayout>
  );
};
